import {
    questionnaireStatus,
    type QuestionnaireStatus,
} from "@app/screens/ExternalQuestionnaires/types";
import type { IconName } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import {
    AiAnswerStatusFilter,
    AnswerStatus,
    QuestionStatusFilter,
} from "@generated/client/graphql";
import { useMemo } from "react";
import { useQuestionsTabTranslations } from "./i18n";

type StatusItem = {
    label: string;
    value: string;
    icon: IconName;
    iconColor?: string;
};

const questionStatuses: StatusItem[] = [
    {
        label: "question_status_not_started",
        value: QuestionStatusFilter.NotStarted,
        icon: "circleTodo",
    },
    {
        label: "question_status_in_progress",
        value: QuestionStatusFilter.InProgress,
        icon: "circleInProgress",
    },
    {
        label: "question_status_validated",
        value: QuestionStatusFilter.Validated,
        icon: "circleValidated",
    },
];

const aiAnswerStatuses: StatusItem[] = [
    {
        label: "question_status_not_started",
        value: AiAnswerStatusFilter.NotStarted,
        icon: "minus",
        iconColor: "!text-primary",
    },
    {
        label: "question_status_ai_suggestion_success",
        value: AiAnswerStatusFilter.AiSuggestionSuccess,
        icon: "stars",
        iconColor: "!text-accent-1-400",
    },
    {
        label: "question_status_ai_suggestion_failed",
        value: AiAnswerStatusFilter.AiSuggestionFailed,
        icon: "warning",
        iconColor: "!text-warning-600",
    },
];

const AnswerStatuses: StatusItem[] = [
    {
        label: "question_status_not_started",
        value: AnswerStatus.NotAnswered,
        icon: "circleTodo",
    },
    {
        label: "question_status_in_progress",
        value: AnswerStatus.Answered,
        icon: "circleInProgress",
    },
    {
        label: "question_status_validated",
        value: AnswerStatus.Validated,
        icon: "circleValidated",
    },
];

const questionnaireStatuses: StatusItem[] = [
    {
        label: "questionnaire_status_todo",
        value: questionnaireStatus.TODO,
        icon: "circleTodo",
    },
    {
        label: "questionnaire_status_in_progress",
        value: questionnaireStatus.IN_PROGRESS,
        icon: "circleInProgress",
    },
    {
        label: "questionnaire_status_validated",
        value: questionnaireStatus.VALIDATED,
        icon: "circleValidated",
    },
];

export const QuestionStatusMultiSelect = <
    T extends
        | QuestionStatusFilter
        | AiAnswerStatusFilter
        | AnswerStatus
        | QuestionnaireStatus,
>({
    value,
    onValueChange,
    statusType,
    triggerLabel,
    preventCloseOnSelect = true,
    isFilter,
}: {
    value: T[];
    onValueChange: (v: T[]) => void;
    statusType: "question" | "aiAnswer" | "answer" | "questionnaire";
    triggerLabel?: string;
    isFilter?: boolean;
    preventCloseOnSelect?: boolean;
}) => {
    const handleStatusChange = (values: (string | null)[]) => {
        onValueChange(values.filter((id) => id !== null) as T[]);
    };

    const { t } = useQuestionsTabTranslations();
    const statuses =
        statusType === "question"
            ? questionStatuses
            : statusType === "aiAnswer"
              ? aiAnswerStatuses
              : statusType === "questionnaire"
                ? questionnaireStatuses
                : AnswerStatuses;

    const active = !!value?.length;

    const options = useMemo(() => {
        return isFilter
            ? statuses.map((status) => ({
                  id: status.value,
                  name: t(status.label),
              }))
            : [];
    }, [isFilter, statuses, t]);

    return (
        <Dropdown
            value={value || []}
            onValueChange={handleStatusChange}
            closeOnValueChange={!preventCloseOnSelect}
            selectType="multi"
        >
            {!isFilter ? (
                <Dropdown.Trigger asChild>
                    <Button active={active} variant="outlined" size="sm">
                        {triggerLabel}
                        <Button.Icon name={"chevronDown"} />
                    </Button>
                </Dropdown.Trigger>
            ) : (
                <Dropdown.ChipFilterTrigger
                    label={triggerLabel}
                    options={options}
                    valueIdentifierKey="id"
                    valueDisplayKey="name"
                />
            )}
            <Dropdown.Content align="start" hasPortal>
                {statuses?.map((status) => (
                    <Dropdown.SelectableItem
                        key={status.value}
                        value={status.value}
                    >
                        <Dropdown.Checkbox />
                        <Dropdown.ItemIcon
                            name={status.icon}
                            className={status.iconColor}
                        />
                        <Dropdown.ItemText>{t(status.label)}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};
